<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { generateRandomAlphaString } from '@shell/utils/string';

interface NodeTaint {
  key?: string;
  value?: string;
  effect?: string;
}
interface MetadataNode {
  spec?: { taints?: NodeTaint[] };
  customLabels?: string[];
}

export default defineComponent({
  props: {
    node:         { type: Object as PropType<MetadataNode>, required: true },
    taintsLabel:  { type: String, default: 'Taints' },
    labelsLabel:  { type: String, default: 'Labels' },
    detailsLabel: { type: String, default: 'Details' },
  },
  data() {
    return { expanded: false, contentId: `node-metadata-${ generateRandomAlphaString(12) }` };
  },
  computed: {
    taints(): NodeTaint[] {
      return this.node.spec?.taints || [];
    },
    labels(): string[] {
      return this.node.customLabels || [];
    },
  },
  watch: {
    node() {
      this.expanded = false;
    },
  },
  methods: {
    taintText(taint: NodeTaint): string {
      const value = taint.value ? `=${ taint.value }` : '';
      const effect = taint.effect ? `:${ taint.effect }` : '';

      return `${ taint.key || '' }${ value }${ effect }`;
    },
  },
});
</script>

<template>
  <div class="node-metadata">
    <div class="metadata-summary">
      <span>{{ taints.length }} {{ taintsLabel.toLowerCase() }}</span>
      <span
        class="summary-divider"
        aria-hidden="true"
      >·</span>
      <span>{{ labels.length }} {{ labelsLabel.toLowerCase() }}</span>
      <button
        type="button"
        :aria-expanded="expanded"
        :aria-controls="contentId"
        @click.stop="expanded = !expanded"
      >
        {{ detailsLabel }} <i
          class="icon"
          :class="expanded ? 'icon-chevron-up' : 'icon-chevron-down'"
          aria-hidden="true"
        />
      </button>
    </div>
    <div
      v-if="expanded"
      :id="contentId"
      class="metadata-details"
    >
      <section>
        <h3>{{ taintsLabel }}</h3>
        <div
          v-if="taints.length"
          class="metadata-chips"
        >
          <span
            v-for="(taint, index) in taints"
            :key="index"
            class="metadata-chip"
            :title="taintText(taint)"
          >{{ taintText(taint) }}</span>
        </div>
        <p
          v-else
          class="empty-metadata"
        >
          None
        </p>
      </section>
      <section>
        <h3>{{ labelsLabel }}</h3>
        <div
          v-if="labels.length"
          class="metadata-chips"
        >
          <span
            v-for="(label, index) in labels"
            :key="index"
            class="metadata-chip"
            :title="label"
          >{{ label }}</span>
        </div>
        <p
          v-else
          class="empty-metadata"
        >
          None
        </p>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.node-metadata { min-width: 0; padding: 0; }
.metadata-summary { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 12px; line-height: 20px; color: var(--dev-muted, var(--muted)); }
.summary-divider { color: var(--dev-muted, var(--muted)); }
.metadata-summary button { display: inline-flex; align-items: center; gap: 4px; padding: 2px 6px; border: 0; border-radius: 4px; background: transparent; color: var(--link); font: inherit; cursor: pointer; }
.metadata-summary button:hover { background: var(--dev-accent-soft, var(--primary-hover-bg)); }
.metadata-summary button:focus-visible { outline: 2px solid var(--primary-keyboard-focus); outline-offset: 2px; }
.metadata-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr)); gap: 12px; margin-top: 8px; padding: 12px; border: 1px solid var(--dev-border, var(--border)); border-radius: 6px; background: var(--dev-layer, var(--body-bg)); }
h3 { margin: 0 0 7px; color: var(--dev-muted, var(--muted)); font-size: 11px; line-height: 16px; font-weight: 600; }
.metadata-chips { display: flex; flex-wrap: wrap; gap: 5px; min-width: 0; }
.metadata-chip { min-width: 0; max-width: 100%; padding: 3px 6px; border: 1px solid var(--dev-border, var(--border)); border-radius: 3px; background: var(--dev-surface, var(--input-bg)); color: var(--dev-ink, var(--body-text)); font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12px; line-height: 1.4; overflow-wrap: anywhere; white-space: normal; }
.empty-metadata { margin: 0; color: var(--dev-muted, var(--muted)); font-size: 11px; }
</style>
